import React from "react";
import { Link } from "react-router-dom";
import { Heart, Star, Download } from "lucide-react";
import { gbp, img } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { AudioPlayer } from "@/components/AudioPlayer";

export const ProductCard = ({ product, index = 0, dark = false }) => {
  const { addToCart, toggleWishlist, inWishlist } = useCart();
  const saved = inWishlist(product.id);
  const discount = product.compare_at_price && product.compare_at_price > product.price;

  return (
    <div
      className={`group border hover:-translate-y-1 transition-all duration-300 fade-up ${dark ? "border-white/10 bg-[#151A21] hover:border-white/30" : "border-neutral-200 bg-white hover:border-neutral-400"}`}
      style={{ animationDelay: `${(index % 8) * 50}ms` }}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative">
        <Link to={`/product/${product.slug}`} className={`block relative aspect-square overflow-hidden ${dark ? "bg-[#0E1216]" : "bg-neutral-100"}`}>
          <img
            src={img(product.images?.[0], 480)} alt={product.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy" decoding="async"
          />
          <div className="absolute top-2 left-2 flex flex-col items-start gap-1">
            {product.boosted && <span className="bg-[#FFC107] text-black text-[10px] font-bold px-2 py-1 flex items-center gap-1" data-testid={`featured-badge-${product.id}`}>★ FEATURED</span>}
            {discount && <span className="bg-[#E63946] text-white text-[10px] font-bold px-2 py-1">SALE</span>}
            {product.age_restricted && <span className="bg-[#E63946] text-white text-[10px] font-bold px-2 py-1" data-testid={`age-badge-${product.id}`}>18+</span>}
          </div>
          {product.product_type === "digital" && <span className="absolute top-2 right-2 bg-[#0A0A0A] text-white text-[10px] px-2 py-1 flex items-center gap-1"><Download className="w-3 h-3" />DIGITAL</span>}
        </Link>
        {product.audio_preview_url && (
          <div className="absolute bottom-2 right-2 z-10" onClick={(e) => e.preventDefault()}>
            <AudioPlayer src={product.audio_preview_url} compact accent="#1DB954" />
          </div>
        )}
        <Link to={product.vendor_slug ? `/store/${product.vendor_slug}` : "/products"} className="absolute bottom-2 left-2 bg-white/90 backdrop-blur text-[10px] font-medium px-2 py-1 max-w-[70%] truncate hover:bg-[#0047AB] hover:text-white transition-colors z-10 flex items-center gap-1" data-testid="vendor-pill">{product.vendor_verified && <span className="text-[#00875A] font-bold" title="Verified Seller">✓</span>}{product.vendor_name}</Link>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-1 mb-1.5">
          <Star className="w-3 h-3 fill-[#FFC107] text-[#FFC107]" />
          <span className={`text-xs ${dark ? "text-neutral-400" : "text-neutral-600"}`}>{product.rating || "New"}{product.reviews_count ? ` (${product.reviews_count})` : ""}</span>
          <span className={`text-[10px] ml-auto uppercase tracking-wider ${dark ? "text-neutral-500" : "text-neutral-400"}`}>{product.category}</span>
        </div>
        <Link to={`/product/${product.slug}`}>
          <h3 className={`font-medium text-sm leading-snug mb-2 line-clamp-2 transition-colors min-h-[2.5rem] ${dark ? "text-neutral-100 hover:text-[#6EA8FF]" : "hover:text-[#0047AB]"}`} data-testid="product-title">{product.title}</h3>
        </Link>
        <div className="flex items-end justify-between">
          <div className="flex items-baseline gap-2">
            <span className={`font-heading text-lg font-semibold ${dark ? "text-white" : ""}`} data-testid="product-price">{gbp(product.price)}</span>
            {discount && <span className={`text-xs line-through ${dark ? "text-neutral-500" : "text-neutral-400"}`}>{gbp(product.compare_at_price)}</span>}
          </div>
          <button onClick={() => toggleWishlist(product)} data-testid={`wishlist-toggle-${product.id}`} className={`p-1.5 transition-colors ${dark ? "hover:bg-white/10" : "hover:bg-neutral-100"}`}>
            <Heart className={`w-4 h-4 ${saved ? "fill-[#E63946] text-[#E63946]" : "text-neutral-500"}`} strokeWidth={1.5} />
          </button>
        </div>
        <button
          onClick={() => addToCart(product)}
          disabled={product.product_type !== "digital" && product.stock <= 0}
          data-testid={`add-to-cart-${product.id}`}
          className={`w-full mt-3 text-xs py-2.5 font-medium transition-colors ${dark
            ? "bg-white text-black hover:bg-[#0047AB] hover:text-white disabled:bg-white/10 disabled:text-neutral-500"
            : "bg-[#0A0A0A] text-white hover:bg-[#0047AB] disabled:bg-neutral-200 disabled:text-neutral-400"}`}
        >
          {product.product_type !== "digital" && product.stock <= 0 ? "Out of stock" : "Add to cart"}
        </button>
      </div>
    </div>
  );
};
